import { PCImage } from 'app/design/primitives/image';
import { Modal } from 'app/design/primitives/modal';
import { Text } from 'app/design/primitives/text';
import React, { useState } from 'react';
import { Pressable, StatusBar, View } from 'react-native';
import ImageViewer from 'react-native-image-zoom-viewer';
import { ArrowRight } from '../icons/arrow-right';
import { isMobileNative } from 'app/utils/web-helpers';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface ImageViewerProps {
  open: boolean;
  onClose: () => void;
  imageUrls: { url: string }[];
  index?: number;
}

function ImageViewerModalHeader({ onPress }: { onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={{
        padding: 16,
        position: 'absolute',
        top: theme.spacing['spacing.general.x8'],
        left: theme.spacing['spacing.general.x4'],
        zIndex: 1,
      }}
    >
      <IconX strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.default']} />
    </Pressable>
  );
}

export default function ImageViewerModal({ open, onClose, imageUrls, index = 0 }: ImageViewerProps) {
  return (
    <ImageViewerModalContent
      key={`${open ? 'open' : 'closed'}-${String(index)}`}
      {...{ open, onClose, imageUrls, index }}
    />
  );
}

function ImageViewerModalContent({ open, onClose, imageUrls, index }: Required<ImageViewerProps>) {
  const [currentIndex, setCurrentIndex] = useState(index);

  const renderArrowLeft = () => {
    if (isMobileNative || currentIndex === 0 || imageUrls.length <= 1) return <View />;
    return <ArrowRight reverse color="white" arrowColor="black" />;
  };

  const renderArrowRight = () => {
    if (isMobileNative || currentIndex === imageUrls.length - 1 || imageUrls.length <= 1) return <View />;
    return <ArrowRight color="white" arrowColor="black" />;
  };

  return (
    <Modal onClose={onClose} open={open} statusBarTranslucent transparent={false}>
      <StatusBar hidden />
      <ImageViewer
        enablePreload
        enableSwipeDown
        renderArrowLeft={renderArrowLeft}
        renderArrowRight={renderArrowRight}
        imageUrls={imageUrls}
        index={currentIndex}
        onChange={(i) => setCurrentIndex(i ?? 0)}
        onClick={onClose}
        onSwipeDown={onClose}
        renderHeader={() => <ImageViewerModalHeader onPress={onClose} />}
        renderIndicator={() => <View />}
        renderImage={(props: ImageRenderProps) => (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <PCImage {...props} contentFit="contain" style={{ ...props.style, borderRadius: 6 }} />
          </View>
        )}
        doubleClickInterval={200}
        saveToLocalByLongPress={false}
        backgroundColor="#00021ee8"
      />
      {imageUrls.length > 1 && (
        <View
          style={{
            position: 'absolute',
            bottom: 40,
            left: 0,
            right: 0,
            flexDirection: 'row',
            justifyContent: 'center',
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              backgroundColor: 'rgba(0,0,0,0.35)',
              borderRadius: 99,
              paddingHorizontal: imageUrls.length > 5 ? 9 : 7,
              paddingVertical: 5,
            }}
          >
            {imageUrls.length > 5 ? (
              <Text style={{ color: '#ffffff', fontSize: 11, fontWeight: '600', lineHeight: 14 }}>
                {currentIndex + 1} / {imageUrls.length}
              </Text>
            ) : (
              imageUrls.map((img, i) => (
                <View
                  key={img.url}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: i === currentIndex ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
                  }}
                />
              ))
            )}
          </View>
        </View>
      )}
    </Modal>
  );
}

interface ImageRenderProps {
  source: { uri: string };
  style: {
    height: number;
    width: number;
  };
}
